import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PageContainer } from "../components/Container/PageContainer";
import { useTranslation } from "react-i18next";
import { getMyCollections, getUserInsight } from "../utils/pureDataFetch";
import { Stack, Text, Avatar, Title, Grid, Flex, Box } from "@mantine/core";
import DataLoader from "../components/DataLoader";
import { CardMyCollection } from "../components/Card/CardMyCollection";
import { Section } from "../components/Container/Section";
import { SectionTitleWithExp } from "../components/customTypo/SectionTitleWithExp";
import { CardContainerRetro } from "../components/Container/CardContainerRetro";
import colorsCustom from "../designTokens/colorsCustom";

const UserPage = ({ isMobile }) => {
	const { t } = useTranslation();
	const { userID } = useParams();
	const [userProfile, setUserProfile] = useState(null);
	const [myCollections, setMyCollections] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// 유저 데이터 및 컬렉션 로드
	useEffect(() => {
		const fetchUserData = async () => {
			try {
				setLoading(true);
				console.log("UserPage - 데이터 로딩 시작, userID:", userID);

				// 사용자 인사이트 정보 가져오기 (Firestore에서)
				try {
					const insightData = await getUserInsight(userID);
					console.log("UserPage - insightData:", insightData);
					if (insightData && insightData.profile) {
						setUserProfile(insightData.profile);
						console.log("UserPage - 프로필 설정됨:", insightData.profile);
					} else {
						console.log("UserPage - 프로필 데이터 없음");
					}
				} catch (profileError) {
					console.error("UserPage - 프로필 로딩 오류:", profileError);
				}

				// 사용자 컬렉션 가져오기
				try {
					const collections = await getMyCollections(userID);
					console.log("UserPage - 전체 컬렉션:", collections);

					// isPublic이 명시적으로 false가 아닌 컬렉션만 표시
					// (isPublic === true 또는 isPublic 필드가 없는 경우 모두 표시)
					const displayCollections = collections.filter(collection => {
						const isPublic = collection.isPublic;
						console.log("컬렉션 확인:", collection.title, "isPublic:", isPublic);
						// isPublic이 명시적으로 false가 아니면 표시 (undefined, null, true 모두 포함)
						return isPublic !== false;
					});

					console.log("UserPage - 표시할 컬렉션:", displayCollections.length, "/", collections.length);
					setMyCollections(displayCollections);
				} catch (collectionError) {
					console.error("UserPage - 컬렉션 로딩 오류:", collectionError);
					setMyCollections([]);
				}

			} catch (error) {
				console.error("UserPage - 전체 로딩 오류:", error);
				setError("사용자 정보를 불러올 수 없습니다.");
			} finally {
				setLoading(false);
			}
		};

		if (userID) {
			fetchUserData();
		}
	}, [userID]);

	if (loading) {
		return (
			<PageContainer>
				<DataLoader msg="사용자 정보를 불러오는 중..." isLoad={true} />
			</PageContainer>
		);
	}

	if (error) {
		return (
			<PageContainer>
				<Stack align="center" gap={24} pt={100}>
					<Text size="xl" c="red">{error}</Text>
				</Stack>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
				{/* 유저 기본정보 섹션 */}
				{userProfile && (
					<Section>
						<SectionTitleWithExp
							isMobile={isMobile}
							title={`${userProfile.username}'s Profile`}
						/>
						<CardContainerRetro
							bg={colorsCustom.paper}
							w={"100%"}
							weight="bold"
							rd={16}
						>
							<Flex
								w={"100%"}
								p={isMobile ? "24px" : "32px"}
								gap={24}
								direction={isMobile ? "column" : "row"}
								align={"flex-start"}
							>
								<Box
									style={{
										borderRadius: "50%",
										padding: 8,
										border: `4px solid ${colorsCustom.ui.dark.bold}`,
									}}
								>
									<Avatar
										size={isMobile ? 80 : 100}
										src={userProfile?.threads_profile_picture_url}
									/>
								</Box>
								<Stack gap={16} align={"flex-start"}>
									<Stack gap={8} align={"flex-start"}>
										<Title order={isMobile ? 3 : 2} fw={700}>
											@{userProfile.username}
										</Title>
										{userProfile.name && (
											<Text size={isMobile ? "lg" : "xl"} fw={500}>
												{userProfile.name}
											</Text>
										)}
									</Stack>
									{userProfile.threads_biography && (
										<Text
											size={isMobile ? "md" : "lg"}
											style={{ textAlign: "left" }}
										>
											{userProfile.threads_biography}
										</Text>
									)}
								</Stack>
							</Flex>
						</CardContainerRetro>
					</Section>
				)}

				{/* 컬렉션 리스트 섹션 */}
				{myCollections.length > 0 && (
					<Section>
						<SectionTitleWithExp
							isMobile={isMobile}
							title={`Public Collections (${myCollections.length})`}
							exp={`${userProfile?.username}님이 공유한 컬렉션들을 확인해보세요!`}
						/>
						<Grid gutter="md">
							{myCollections.map((collection, index) => (
								<Grid.Col
									key={`${collection.id || index}-grid`}
									span={isMobile ? 6 : 4}
								>
									<Box h="100%">
										<CardMyCollection
											collection={collection}
											isMobile={isMobile}
											isMe={false}
											style={{ height: "100%" }}
										/>
									</Box>
								</Grid.Col>
							))}
						</Grid>
					</Section>
				)}

				{myCollections.length === 0 && userProfile && (
					<Section>
						<Stack align="center" gap={16}>
							<Text size="lg" c="dimmed">
								{userProfile.username}님이 아직 공개한 컬렉션이 없습니다.
							</Text>
						</Stack>
					</Section>
				)}
		</PageContainer>
	);
};

export default UserPage;
